/**
 * Public facility policies-values read (DNKADM-0192 U1 / DNKAPI-0319).
 *
 * The endpoint is intentionally public and facility-level. It lives on dink-api's
 * `/v2/public/**` transport: no cookies and no CSRF. The shared client may attach the
 * console's in-memory bearer because `/v2/**` is its established dink-api routing
 * seam; DNKAPI-0319 ignores that bearer on this permit-all endpoint.
 */
import { request } from './client';
import { ApiError } from './errors';
import { v2ErrorCode } from './v2Errors';

type PublicPolicyBandScope = 'ALL' | 'BUCKET';

interface PublicPolicyBand {
  scope: PublicPolicyBandScope;
  bucketId: string | null;
  bucketName: string | null;
  minHoursBefore: number;
  refundPercent: number;
}

/** Null scalar values represent an absent or unusable field in a successful wire body. */
interface PoliciesBundle {
  tenant: string | null;
  currency: string | null;
  booking: {
    windowMonths: number | null;
    serviceIntervalMinutes: number | null;
    guestHoldTtlMinutes: number | null;
  };
  cancellation: {
    defaultFullRefundMinHours: number | null;
    refundRoundingIncrement: number | null;
    refundRoundingMode: string | null;
    bands: PublicPolicyBand[];
  };
  membership: {
    graceDays: number | null;
    /** `null` is the DNKAPI-0319 wire contract for unlimited reschedules. */
    maxReschedules: number | null;
  };
  reservation: {
    reconciliationGraceMinutes: number | null;
    deferredChargeMinutes: number | null;
  };
}

export type PoliciesResult =
  | { status: 'available'; data: PoliciesBundle }
  | { status: 'unavailable'; reason: 'UNKNOWN_TENANT' };

function isObject(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null;
}

function objectOrEmpty(value: unknown): Record<string, unknown> {
  return isObject(value) ? value : {};
}

function asText(value: unknown): string | null {
  if (typeof value !== 'string') return null;
  const text = value.trim();
  return text.length > 0 ? text : null;
}

/** Accept JSON numbers and BigDecimal-style numeric strings; reject infinities and blanks. */
function asNumber(value: unknown): number | null {
  if (typeof value === 'number') return Number.isFinite(value) ? value : null;
  if (typeof value === 'string' && value.trim().length > 0) {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : null;
  }
  return null;
}

function asId(value: unknown): string | null {
  if (typeof value === 'number' && Number.isFinite(value)) return String(value);
  return asText(value);
}

function parseBand(value: unknown): PublicPolicyBand | null {
  if (!isObject(value)) return null;
  const scope = value['scope'];
  const minHoursBefore = asNumber(value['minHoursBefore']);
  const refundPercent = asNumber(value['refundPercent']);
  if (
    (scope !== 'ALL' && scope !== 'BUCKET') ||
    minHoursBefore === null ||
    refundPercent === null
  ) {
    return null;
  }
  return {
    scope,
    bucketId: asId(value['bucketId']),
    bucketName: asText(value['bucketName']),
    minHoursBefore,
    refundPercent,
  };
}

/** Parse every group without trusting nesting or scalar wire types. Never throws. */
function parsePolicies(value: unknown): PoliciesBundle {
  const body = objectOrEmpty(value);
  const booking = objectOrEmpty(body['booking']);
  const cancellation = objectOrEmpty(body['cancellation']);
  const membership = objectOrEmpty(body['membership']);
  const reservation = objectOrEmpty(body['reservation']);
  const rawBands = Array.isArray(cancellation['bands']) ? cancellation['bands'] : [];

  return {
    tenant: asText(body['tenant']),
    currency: asText(body['currency']),
    booking: {
      windowMonths: asNumber(booking['windowMonths']),
      serviceIntervalMinutes: asNumber(booking['serviceIntervalMinutes']),
      guestHoldTtlMinutes: asNumber(booking['guestHoldTtlMinutes']),
    },
    cancellation: {
      defaultFullRefundMinHours: asNumber(cancellation['defaultFullRefundMinHours']),
      refundRoundingIncrement: asNumber(cancellation['refundRoundingIncrement']),
      refundRoundingMode: asText(cancellation['refundRoundingMode']),
      bands: rawBands.map(parseBand).filter((band): band is PublicPolicyBand => band !== null),
    },
    membership: {
      graceDays: asNumber(membership['graceDays']),
      maxReschedules: asNumber(membership['maxReschedules']),
    },
    reservation: {
      reconciliationGraceMinutes: asNumber(reservation['reconciliationGraceMinutes']),
      deferredChargeMinutes: asNumber(reservation['deferredChargeMinutes']),
    },
  };
}

/**
 * Read the tenant's consolidated public policy values. A DNKAPI-0319
 * `404 UNKNOWN_TENANT` is a normal unavailable state; every other failure remains a
 * typed `ApiError` for the surface's ordinary error state.
 */
export async function getPolicies(tenantSlug: string): Promise<PoliciesResult> {
  const path = `/v2/public/${encodeURIComponent(tenantSlug)}/policies`;
  try {
    const body = await request<unknown>('GET', path);
    return { status: 'available', data: parsePolicies(body) };
  } catch (error) {
    if (
      error instanceof ApiError &&
      error.status === 404 &&
      v2ErrorCode(error) === 'UNKNOWN_TENANT'
    ) {
      return { status: 'unavailable', reason: 'UNKNOWN_TENANT' };
    }
    throw error;
  }
}
